import { useCallback, useMemo, useState } from "react";
import { Box, Paper } from "@mui/material";
import { ErrorModal } from "@dexteel/mesf-core";
import {
	Group,
	Panel,
	Separator,
	useDefaultLayout,
} from "react-resizable-panels";
import { CenteredLazyLoading } from "@/controls/LazyLoading";
import { NotificationSnackBar } from "@/controls/snackbars/notification-snackbar";
import {
	urlParam,
	useDropUnknownValue,
	useUrlState,
} from "@/shared/hooks/useUrlState";
import { useUrlSelection } from "@/shared/hooks/useUrlSelection";
import {
	useRollsetStatusOptions,
	useSetRollsetStatus,
} from "../hooks/useRollshopOperations";
import {
	usePassStatus,
	useRollshopRollsets,
	useRollsetStatusValidChanges,
	useRollsProduction,
	useUpdatePassStatus,
} from "../hooks/useRollshop";
import type { RollsetListItem } from "../models/RollshopOperations";
import type { RollsProductionRow } from "../models/Rollshop";
import { RollshopDetailPanel } from "./RollshopDetailPanel";
import { RollshopTable } from "./RollshopTable";
import { RollshopToolbar } from "./RollshopToolbar";
import { PassConditionModal } from "./PassConditionModal";
import { TurnRollsModal } from "./TurnRollsModal";

const DEFAULT_LAYOUT = { left: 50, right: 50 };

const URL_SCHEMA = {
	statusCode: urlParam.string(),
	rollsetId: urlParam.int(),
};

export const Rollshop = () => {
	const [{ statusCode, rollsetId }, setFilters, { dropValues }] =
		useUrlState(URL_SCHEMA);
	const [error, setError] = useState("");
	const [notification, setNotification] = useState("");
	const [turnOpen, setTurnOpen] = useState(false);
	const [statusMenuOpen, setStatusMenuOpen] = useState(false);
	const [editingPass, setEditingPass] = useState<RollsProductionRow | null>(
		null,
	);
	const [passError, setPassError] = useState("");
	const [isRefreshing, setIsRefreshing] = useState(false);
	// Bumped by a pick in the search: the list scrolls to that row and takes the focus.
	const [revealKey, setRevealKey] = useState(0);
	// Like the Delays Manager: no refresh under an open menu or modal.
	const isRefreshHeld = turnOpen || statusMenuOpen || editingPass !== null;

	const rollsetsQuery = useRollshopRollsets(isRefreshHeld);
	const { data: statusOptions = [], isSuccess: statusOptionsLoaded } =
		useRollsetStatusOptions();
	const { data: statusChanges = [] } = useRollsetStatusValidChanges();
	const { data: passStatus = [] } = usePassStatus();

	useDropUnknownValue(
		statusCode,
		statusOptionsLoaded,
		statusOptions.map((o) => o.Code),
		useCallback(() => dropValues("statusCode"), [dropValues]),
	);

	// Legacy lists active rollsets only.
	const activeRollsets = useMemo(
		() => (rollsetsQuery.data ?? []).filter((r) => r.Active !== false),
		[rollsetsQuery.data],
	);

	const visibleRollsets = useMemo(
		() =>
			activeRollsets.filter(
				(r) => statusCode == null || r.StatusCode === statusCode,
			),
		[activeRollsets, statusCode],
	);

	const selection = useUrlSelection<RollsetListItem>({
		id: rollsetId,
		setId: (value) => setFilters({ rollsetId: value }),
		dropId: () => dropValues("rollsetId"),
		rows: activeRollsets,
		ready: rollsetsQuery.isSuccess,
		getId: (row) => row.Id,
	});
	const selected = selection.current;

	const productionQuery = useRollsProduction(
		selected?.Id ?? null,
		isRefreshHeld,
	);

	const { setStatus, isSettingStatus } = useSetRollsetStatus({
		onSuccess: () => setNotification("Op. Status updated"),
		onError: (e) => setError(e.message),
	});
	const { updatePassStatus, isUpdatingPassStatus } = useUpdatePassStatus({
		onSuccess: () => {
			setEditingPass(null);
			setNotification("Pass status updated");
		},
		onError: (e) => setPassError(e.message),
	});

	const { defaultLayout, onLayoutChange } = useDefaultLayout({
		id: "rollshop-v1",
		panelIds: ["left", "right"],
		storage: localStorage,
	});

	const handleSetStatus = useCallback(
		(code: string) => {
			if (selected) setStatus({ id: selected.Id, statusCode: code });
		},
		[selected, setStatus],
	);

	const handleEditPass = useCallback((row: RollsProductionRow) => {
		setPassError("");
		setEditingPass(row);
	}, []);

	if (rollsetsQuery.error && !error) {
		setError(
			rollsetsQuery.error instanceof Error
				? rollsetsQuery.error.message
				: "Error fetching rollsets",
		);
	}

	return (
		<Box
			sx={{
				flex: 1,
				display: "flex",
				flexDirection: "column",
				minHeight: 0,
				gap: 1,
			}}
		>
			<Paper elevation={3}>
				<RollshopToolbar
					rollsets={visibleRollsets}
					pickedRollset={selected}
					onRollsetPicked={(row) => {
						selection.select(row);
						setRevealKey((key) => key + 1);
					}}
					statusCode={statusCode}
					onStatusChange={(code) => setFilters({ statusCode: code })}
					statusOptions={statusOptions}
					onRefresh={() => {
						// Only the button's own refresh shows; the 10 s one stays quiet.
						setIsRefreshing(true);
						Promise.all([
							rollsetsQuery.refetch(),
							productionQuery.refetch(),
						]).finally(() => setIsRefreshing(false));
					}}
					isLoading={isRefreshing}
				/>
			</Paper>

			<Group
				id="rollshop-master-detail"
				orientation="horizontal"
				defaultLayout={defaultLayout ?? DEFAULT_LAYOUT}
				onLayoutChange={onLayoutChange}
				style={{ flex: 1, width: "100%" }}
			>
				<Panel id="left" minSize={30} style={{ paddingBottom: 5 }}>
					<Box sx={{ height: "100%", pr: 1 }}>
						<Paper elevation={3} sx={{ height: "100%", overflow: "hidden" }}>
							{rollsetsQuery.isLoading ? (
								<CenteredLazyLoading />
							) : (
								<RollshopTable
									rows={visibleRollsets}
									selectedId={selected?.Id ?? null}
									revealKey={revealKey}
									onRowSelected={selection.select}
								/>
							)}
						</Paper>
					</Box>
				</Panel>

				<Separator
					style={{
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
						cursor: "col-resize",
					}}
				>
					<Box
						sx={{
							width: 6,
							height: 40,
							borderRadius: 3,
							backgroundColor: "divider",
							"&:hover": { backgroundColor: "primary.main" },
						}}
					/>
				</Separator>

				<Panel id="right" minSize={30} style={{ paddingBottom: 5 }}>
					<Box sx={{ height: "100%", pl: 1 }}>
						<Paper elevation={3} sx={{ height: "100%", overflow: "hidden" }}>
							<RollshopDetailPanel
								rollset={selected}
								production={productionQuery.data ?? []}
								isLoadingProduction={productionQuery.isInitialLoading}
								passStatus={passStatus}
								statusChanges={statusChanges}
								isSettingStatus={isSettingStatus}
								onTurn={() => setTurnOpen(true)}
								onSetStatus={handleSetStatus}
								onEditPass={handleEditPass}
								onClose={selection.clear}
								onStatusMenuOpenChange={setStatusMenuOpen}
							/>
						</Paper>
					</Box>
				</Panel>
			</Group>

			<TurnRollsModal
				open={turnOpen}
				rollset={selected}
				onClose={() => setTurnOpen(false)}
				onTurned={() => {
					setTurnOpen(false);
					setNotification("Rolls turned");
				}}
			/>

			<PassConditionModal
				row={editingPass}
				rollsetName={selected?.Name ?? ""}
				passStatus={passStatus}
				isSaving={isUpdatingPassStatus}
				error={passError}
				onClose={() => setEditingPass(null)}
				onSave={(row, code) => {
					setPassError("");
					updatePassStatus({
						idBottom: row.IdBottom,
						idTop: row.IdTop,
						passStatusCode: code,
					});
				}}
			/>

			<NotificationSnackBar
				message={notification}
				onHide={() => setNotification("")}
			/>
			<ErrorModal error={error} onHide={() => setError("")} />
		</Box>
	);
};
